// src/features/account/pages/AccountDashboard.jsx
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useWorkOS } from '../../workos/context/WorkOSContext';
import { useSearchParams } from 'react-router-dom';
import { CreditCard, Coins, User, Key, DollarSign, Crown } from 'lucide-react';

import ProfileTab from '../components/ProfileTab';
import PaymentMethodTab from '../components/PaymentMethodTab';
import CreditsTab from '../components/CreditsTab';
import ApiKeysTab from '../components/ApiKeysTab';
import CreatorEarningsTab from '../components/CreatorEarningsTab';
import SubscriptionTab from '../components/SubscriptionTab';

const AccountDashboard = () => {
  const { user } = useWorkOS();
  const [searchParams] = useSearchParams();

  // Get tab from URL parameter, default to 'profile'
  const initialTab = searchParams.get('tab') || 'profile';
  const [activeTab, setActiveTab] = useState(initialTab);

  const [dataReady, setDataReady] = useState(false);

  // Update activeTab when URL parameter changes
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);

  useEffect(() => {
    setDataReady(true);
  }, [user]);

  const tabs = {
    profile: {
      icon: <User className="w-5 h-5" />,
      title: 'Profile',
    },
    subscription: {
      icon: <Crown className="w-5 h-5" />,
      title: 'Subscription',
    },
    payment: {
      icon: <CreditCard className="w-5 h-5" />,
      title: 'Payment Method',
    },
    credits: {
      icon: <Coins className="w-5 h-5" />,
      title: 'Balance',
    },
    earnings: {
      icon: <DollarSign className="w-5 h-5" />,
      title: 'Earnings',
    },
    apikeys: {
      icon: <Key className="w-5 h-5" />,
      title: 'API Keys',
    }
  };

  // Show skeleton until data is ready
  if (!dataReady) {
    return (
      <>
        <Helmet>
          <title>My Account - ContextUI</title>
          <meta name="description" content="Manage your ContextUI account and balance." />
          <meta name="robots" content="noindex, nofollow" />
          <link rel="canonical" href="https://contextui.ai/account" />
        </Helmet>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Profile Header Skeleton */}
        <div className="flex items-center space-x-4 mb-8 animate-pulse">
          <div className="w-16 h-16 bg-gray-800 rounded-full" />
          <div className="space-y-2">
            <div className="h-6 w-32 bg-gray-800 rounded" />
            <div className="h-4 w-48 bg-gray-800 rounded" />
          </div>
        </div>

        {/* Tabs Skeleton */}
        <div className="flex space-x-1 mb-8 bg-gray-900 rounded-lg p-1">
          <div className="flex-1 h-10 bg-gray-800 rounded animate-pulse" />
          <div className="flex-1 h-10 bg-gray-800 rounded animate-pulse" />
        </div>

        {/* Content Skeleton */}
        <div className="bg-gray-900 rounded-lg p-6">
          <div className="space-y-4">
            <div className="h-6 w-32 bg-gray-800 rounded animate-pulse" />
            <div className="h-4 w-full bg-gray-800 rounded animate-pulse" />
            <div className="h-4 w-3/4 bg-gray-800 rounded animate-pulse" />
          </div>
        </div>
      </div>
      </>
    );
  }

  // Main render - all data is ready
  return (
    <>
      <Helmet>
        <title>My Account - ContextUI</title>
        <meta name="description" content="Manage your ContextUI account and balance." />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href="https://contextui.ai/account" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Profile Header */}
      <div className="flex items-center space-x-4 mb-8">
        {user?.profilePictureUrl ? (
          <img
            src={user.profilePictureUrl}
            alt={user.firstName || 'Profile'}
            className="w-16 h-16 rounded-full"
          />
        ) : (
          <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-xl font-bold">
            {user?.firstName?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || '?'}
          </div>
        )}

        <div>
          <h1 className="text-2xl font-bold text-white">
            {user?.firstName && user?.lastName
              ? `${user.firstName} ${user.lastName}`
              : user?.email || 'User'}
          </h1>
          <p className="text-gray-400">{user?.email}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 mb-8 bg-gray-900 rounded-lg p-1">
        {Object.entries(tabs).map(([key, tab]) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-md transition-colors ${
              activeTab === key
                ? 'bg-gray-700 text-white'
                : 'text-gray-400 hover:text-white hover:bg-gray-800'
            }`}
          >
            {tab.icon}
            <span>{tab.title}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'profile' && (
        <ProfileTab />
      )}

      {activeTab === 'subscription' && (
        <div className="bg-gray-900 rounded-lg p-6 border border-gray-800">
          <SubscriptionTab />
        </div>
      )}

      {activeTab === 'earnings' && (
        <div className="bg-gray-900 rounded-lg p-6 border border-gray-800">
          <CreatorEarningsTab />
        </div>
      )}

      {activeTab === 'apikeys' && (
        <div className="bg-gray-900 rounded-lg p-6 border border-gray-800">
          <ApiKeysTab />
        </div>
      )}

      <div className={`bg-gray-900 rounded-lg p-6 ${activeTab === 'profile' || activeTab === 'apikeys' || activeTab === 'earnings' || activeTab === 'subscription' ? 'hidden' : ''}`}>
        {activeTab === 'payment' && (
          <PaymentMethodTab />
        )}

        {activeTab === 'credits' && (
          <CreditsTab />
        )}
      </div>
      </div>
    </>
  );
};

export default AccountDashboard;
